import { motion } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMemo } from "react";
import { FuelMenuNav } from "@/components/fuel-activity/FuelMenuNav";
import { FuelMonthTabs } from "@/components/fuel-activity/FuelMonthSummaryCards";
import { MonthlySpendBreakdownView } from "@/components/fuel-activity/MonthlySpendBreakdownView";
import { PageHeader } from "@/components/ui/PageHeader";
import { ExplorerSkeleton } from "@/components/ui/PageSkeleton";
import { SafeImage } from "@/components/ui/SafeImage";
import { getAvailableFuelMonths, getMonthlySpendBreakdown } from "@/lib/fuel-analytics";
import { useUserFuelActivity } from "@/lib/queries";
import { imageSrc, userDisplayName } from "@/lib/utils";

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
};

export function UserFuelSpendMonthPage() {
  const { userId = "", year: yearParam, month: monthParam } = useParams();
  const year = Number(yearParam);
  const month = Number(monthParam) - 1;
  const { data, isLoading, error } = useUserFuelActivity(userId);

  const user = data?.user ?? null;
  const name = user ? userDisplayName(user) : userId.slice(0, 8);
  const profilePhoto = user ? imageSrc(user.profilePhoto) : null;
  const basePath = `/fuel-activity/users/${userId}/spend`;

  const availableMonths = useMemo(
    () => (data ? getAvailableFuelMonths(data.logs, year) : []),
    [data, year]
  );

  const breakdown = useMemo(() => {
    if (!data || !Number.isFinite(year) || !Number.isFinite(month)) return null;
    const userList = user ? [user] : [];
    return getMonthlySpendBreakdown(data.logs, userList, data.cars, data.stations, year, month);
  }, [data, user, year, month]);

  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 0 || month > 11) {
    return <Navigate to={`${basePath}`} replace />;
  }

  if (!isLoading && data && availableMonths.length > 0 && !availableMonths.includes(month)) {
    const fallback = availableMonths[availableMonths.length - 1];
    return <Navigate to={`${basePath}/${year}/${fallback + 1}`} replace />;
  }

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="space-y-6"
    >
      <div className="flex flex-wrap items-center gap-4">
        <Link
          to={`/fuel-activity/users/${userId}`}
          className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
        >
          ← {name}
        </Link>
        <Link
          to={`${basePath}/${year}`}
          className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
        >
          ← {year} overview
        </Link>
        <Link
          to={basePath}
          className="inline-flex text-sm font-semibold text-muted transition hover:text-foreground"
        >
          All years
        </Link>
      </div>

      <PageHeader
        title={breakdown ? `${name} · ${breakdown.label}` : "Monthly spend"}
        description="Fuel spend breakdown for this month."
        showGreeting={false}
        action={
          profilePhoto ? (
            <SafeImage
              src={profilePhoto}
              alt={name}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-subtle text-sm font-bold text-accent-foreground">
              {name.charAt(0).toUpperCase()}
            </div>
          )
        }
      />

      <FuelMenuNav />

      {availableMonths.length > 0 ? (
        <FuelMonthTabs months={availableMonths} year={year} activeMonth={month} basePath={basePath} />
      ) : null}

      {isLoading ? (
        <ExplorerSkeleton title="Monthly spend" />
      ) : error || !data || !breakdown ? (
        <p className="text-sm text-red-600">Failed to load monthly spend breakdown.</p>
      ) : (
        <MonthlySpendBreakdownView breakdown={breakdown} scope="user" />
      )}
    </motion.div>
  );
}
